/**
 * WorkQueueSection.js
 *
 * Phase 2: Work Queue section for Technician Dashboard
 * Displays assigned complaints in table format with sorting
 *
 * Features:
 * - Sorting by: Date (default), Severity, Status, Age, Device
 * - Click row to navigate to complaint detail page for status updates
 *
 * Props:
 * - assignedComplaints: Array of complaint objects assigned to the user
 * - token: JWT authentication token
 * - onComplaintClick: Callback when clicking a complaint row
 */

import React, { useState } from 'react';
import { ComplaintIDBadge, DeviceIDBadge, StatusBadge, SeverityBadge } from '../../../shared/components/common/Badges';

const WorkQueueSection = ({ assignedComplaints = [], token, onComplaintClick }) => {
  const [sortBy, setSortBy] = useState('date');
  const [localComplaints, setLocalComplaints] = useState(assignedComplaints);

  // Update local complaints when prop changes
  React.useEffect(() => {
    setLocalComplaints(assignedComplaints);
  }, [assignedComplaints]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysSince = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const today = new Date();
    const days = Math.floor((today - date) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const getSortedComplaints = () => {
    let sorted = [...localComplaints];

    switch (sortBy) {
      case 'severity':
        const severityOrder = { critical: 0, major: 1, minor: 2 };
        sorted.sort((a, b) => severityOrder[a.severity || 'minor'] - severityOrder[b.severity || 'minor']);
        break;

      case 'age':
        sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;

      case 'device':
        sorted.sort((a, b) => (a.device_name || '').localeCompare(b.device_name || ''));
        break;

      case 'status':
        const statusOrder = { open: 0, acknowledged: 1, in_progress: 2, escalated_to_vendor: 3, resolved: 4 };
        sorted.sort((a, b) => statusOrder[a.status || 'open'] - statusOrder[b.status || 'open']);
        break;

      case 'date':
      default:
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return sorted;
  };

  const sortedComplaints = getSortedComplaints();

  return (
    <div className="dashboard-section work-queue-section">
      <div className="work-queue-header">
        <h3 className="dashboard-section-title">📋 Assigned Complaints</h3>
        {localComplaints && localComplaints.length > 0 && (
          <div className="work-queue-controls">
            <label htmlFor="sort-select" className="sort-label">Sort by:</label>
            <select
              id="sort-select"
              className="sort-dropdown"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Date (Newest)</option>
              <option value="severity">Severity (Critical → Minor)</option>
              <option value="status">Status (Open → Resolved)</option>
              <option value="age">Age (Oldest First)</option>
              <option value="device">Device (A-Z)</option>
            </select>
          </div>
        )}
      </div>

      {localComplaints && localComplaints.length > 0 ? (
        <div className="work-queue-table-container">
          <table className="work-queue-table">
            <thead>
              <tr>
                <th className="col-rank">#</th>
                <th className="col-complaint">Complaint</th>
                <th className="col-device">Device</th>
                <th className="col-department">Department</th>
                <th className="col-reported">Reported</th>
                <th className="col-severity">Severity</th>
                <th className="col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedComplaints.map((complaint, index) => (
                <tr
                  key={complaint.id}
                  className="complaint-row clickable"
                  onClick={() => onComplaintClick && onComplaintClick(complaint)}
                >
                  <td className="col-rank">
                    <span className="rank-badge">{index + 1}</span>
                  </td>
                  <td className="col-complaint complaint-stacked-cell">
                    <div className="stacked-content">
                      <span className="complaint-title">{complaint.title}</span>
                      <ComplaintIDBadge complaintId={complaint.complaint_identifier || 'N/A'} size="small" />
                    </div>
                  </td>
                  <td className="col-device device-stacked-cell">
                    <div className="stacked-content">
                      <span className="device-name">{complaint.device_name}</span>
                      <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                    </div>
                  </td>
                  <td className="col-department">
                    <span className="department-cell">
                      <span className="department-icon-small" aria-hidden="true">
                        {complaint.department_icon || '🏢'}
                      </span>
                      <span className="department-text">{complaint.department || 'N/A'}</span>
                    </span>
                  </td>
                  <td className="col-reported">
                    <span className="reported-text">{getDaysSince(complaint.created_at)}</span>
                  </td>
                  <td className="col-severity">
                    <SeverityBadge severity={complaint.severity} size="small" />
                  </td>
                  <td className="col-status center">
                    <StatusBadge status={complaint.status || 'unknown'} size="small" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-work-queue-items">
          <p>✅ No assigned complaints</p>
          <p className="subtext">All assigned work is currently up to date</p>
        </div>
      )}
    </div>
  );
};

export default WorkQueueSection;
